import { useEffect, useState } from 'react';

import { Link, useParams } from 'react-router';

import Autoplay from 'embla-carousel-autoplay';

import { TrackViewVariantList } from '@boc/analyticsV3';
import { type RelatedProductsResult } from '@boc/framework/products';
import { type ProductVariantCardFragment } from '@boc/framework/saleor-graphql';
import { RichText } from '@boc/ui';

import { absoluteify, urls } from '~/urls';

import { useProductDetails } from '~/framework/routes/products/providers';

import { Button } from '~/local/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from '~/local/homepage/components/slider';
import VariantCard from '~/local/products/components/variant-card';

function RecommendationSection({ section }: { section: RelatedProductsResult }) {
  const params = useParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Generate a slug-friendly list ID from the section title
  const listId = section.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  // Extract first paragraph for preview (EditorJS format)
  const getPreviewContent = (jsonContent: string) => {
    try {
      const parsed = JSON.parse(jsonContent);

      if (!parsed.blocks || !Array.isArray(parsed.blocks)) {
        return jsonContent;
      }

      // Take first block (first paragraph) for preview
      const previewBlocks = parsed.blocks.slice(0, 1);
      const hasMore = parsed.blocks.length > 1;

      return {
        preview: JSON.stringify({ ...parsed, blocks: previewBlocks }),
        hasMore,
      };
    } catch {
      // If not valid JSON, return as is
      return { preview: jsonContent, hasMore: false };
    }
  };

  const previewContent = section.description
    ? getPreviewContent(section.description)
    : { preview: '', hasMore: false };
  const previewDescription =
    typeof previewContent === 'string' ? previewContent : previewContent.preview;
  const hasMoreContent = typeof previewContent === 'string' ? false : previewContent.hasMore;

  return (
    <div className="my-16">
      <TrackViewVariantList
        variants={section.products}
        itemUrl={(v) => absoluteify(urls.products.details(params, { slug: v.product.slug }))}
        listId={listId}
        listName={section.title}
      />
      <div className="mb-6 flex items-center justify-between">
        {section.url ? (
          <Link to={section.url} className="hover:underline">
            <h3 className="font-heading-condensed text-2xl leading-7 font-medium tracking-tight">
              {section.title}
            </h3>
          </Link>
        ) : (
          <h3 className="font-heading-condensed text-2xl leading-7 font-medium tracking-tight">
            {section.title}
          </h3>
        )}
        {section.url && (
          <Link
            to={section.url}
            className="text-primary-2-60 hover:text-primary-2-80 text-sm font-medium underline"
          >
            View All
          </Link>
        )}
      </div>

      <Carousel
        opts={{
          loop: true,
        }}
        className="mt-8"
        plugins={
          mounted
            ? [
                Autoplay({
                  delay: 2500,
                }),
              ]
            : []
        }
      >
        <CarouselContent>
          {section.products.map((variant: ProductVariantCardFragment, index: number) => (
            <CarouselItem
              key={variant.id || index}
              className="basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
            >
              <VariantCard variant={variant} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
      {section.description && (
        <div className="mt-2 mb-4 text-sm text-gray-600">
          {!isExpanded ? (
            <>
              <RichText className="prose-sm !max-w-none">{previewDescription}</RichText>
              {hasMoreContent && (
                <div className="mt-2 flex justify-end">
                  <Button
                    variant="link"
                    onClick={() => setIsExpanded(true)}
                    className="p-0 text-xs"
                  >
                    Read more
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <RichText className="prose-sm !max-w-none">{section.description}</RichText>
              <div className="mt-2 flex justify-end">
                <Button variant="link" onClick={() => setIsExpanded(false)} className="p-0 text-xs">
                  Show less
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ProductRecommendations() {
  const { relatedProducts } = useProductDetails();

  if (!relatedProducts?.length) {
    return null;
  }

  return (
    <div className="my-10">
      {relatedProducts.map((section, index) => (
        <RecommendationSection key={`${section.title}-${index}`} section={section} />
      ))}
    </div>
  );
}
